import Joi from 'joi';
import moment from 'moment';
import React, { ChangeEvent, Component, KeyboardEvent, } from 'react';
import { RouteComponentProps } from 'react-router';
import GearDTO from '../../../../common/api/dtos/Gear';
import UserDTO from '../../../../common/api/dtos/User';
import { listComputers } from '../../../../common/api/endpoints/computers';
import { addGear, listGearDetails, updateGear } from '../../../../common/api/endpoints/gears';
import { listAllUsers } from '../../../../common/api/endpoints/users';
import AppContext from '../../../../common/context/AppContext';
import { FormErrors } from '../../../../common/data/FormErrors';
import { processJoiError } from '../../../../common/helpers/processJoiError';
import { ComputerDTO } from '../../../../common/interfaces/Computer';
import { TRequestStatus } from '../../../../common/types/RequestStatus';
import ButtonControl from '../../../controls/ButtonControl/ButtonControl';
import ToggleControl from '../../../controls/ToggleControl/ToggleControl';
import BreadcrumbControls from '../../../generics/Header/BreadcrumbControls';
import ProgressBar, { IProgress, incrementProgress } from '../../../utils/ProgressBar/ProgressBar';
import RequestStatus from '../../../utils/RequestStatus/RequestStatus';
import { isEditPage } from '../../../../common/helpers/isEditPage';
import TextAreaControl from '../../../controls/TextAreaControl/TextAreaControl';
import { loadComplete } from '../../../../common/helpers/LoadComplete';
import TextControl from '../../../controls/TextControl/TextControl';
import SelectControl from '../../../controls/SelectControl/SelectControl';
import { withTransitionEvent } from '../../../TransitionEvent';

export interface Props {
  id: string,
}

interface FormData {
  name: string,
  internalId?: string,
  accountingId?: string,
  iconUrl: string,
  description: string,
  comments: string,
  tileColor: string,
  inboundDate?: string,
  outboundDate?: string,
  userId?: number | null,
  computerId?: number | null,
  deleted: boolean,
}

interface State {
  itemId: string,
  progress: IProgress,
  pageStatus: TRequestStatus,
  formStatus: TRequestStatus,
  formData: FormData,
  items?: GearDTO[],
  computers?: ComputerDTO[],
  users?: UserDTO[],
  formErrors: StateFormErrors,
}

type StateFormErrors = {
  [key in keyof FormData]?: string;
}

class ItemDetail extends Component<RouteComponentProps<Props>, State> {
  formSchema = Joi.object({
    name: Joi.string().trim(true).required().messages(FormErrors.string),
    internalId: Joi.string().trim(true).required().messages(FormErrors.string),
    accountingId: Joi.string().trim(true).required().messages(FormErrors.string),
    iconUrl: Joi.string().trim(true).allow(null, ''),
    description: Joi.string().trim(true).allow(null, ''),
    comments: Joi.string().trim(true).allow(null, ''),
    tileColor: Joi.string().trim(true).allow(null, ''),
    inboundDate: Joi.string().trim(true).allow(null, ''),
    outboundDate: Joi.string().trim(true).allow(null, ''),
    userId: Joi.number().allow(null, 0),
    computerId: Joi.number().allow(null, 0),
    deleted: Joi.boolean(),
  });

  constructor(props: RouteComponentProps<Props>) {
    super(props);
    this.state = {
      itemId: this.props.match.params.id,
      formData: {
        name: '',
        internalId: '',
        accountingId: '',
        iconUrl: 'inventory.png',
        description: '',
        inboundDate: moment(new Date()).format("YYYY-MM-DD"),
        comments: '',
        tileColor: '',
        deleted: false,
      },
      progress: {
        currentStep: 0,
        totalSteps: 2
      },
      pageStatus: 'loading',
      formStatus: 'idle',
      formErrors: {},
    }
  }

  fetchItemsDetails = async () => {
    this.setState({
      pageStatus: 'loading'
    });

    const item = await listGearDetails(this.state.itemId);

    this.setState(prevState => {
      return {
        pageStatus: 'success',
        formData: {
          name: item.name,
          internalId: item?.internalId,
          accountingId: item?.accountingId,
          iconUrl: item.iconUrl,
          description: item.description,
          comments: item.comments,
          tileColor: item.tileColor,
          inboundDate: item.inboundDate ? moment(new Date(item.inboundDate)).format('Y-MM-DD') : undefined,
          outboundDate: item.outboundDate ? moment(new Date(item.outboundDate)).format('Y-MM-DD') : undefined,
          userId: item.userId ?? null,
          computerId: item.computerId ? item.computerId : null,
          deleted: item.deleted,
        },
        progress: incrementProgress(prevState.progress),
      }
    });
  }

  fetchUsers = async () => {
    this.setState({
      pageStatus: 'loading'
    });

    const users = await listAllUsers();

    this.setState(prevState => {
      return {
        pageStatus: 'success',
        users,
        progress: incrementProgress(prevState.progress),
      }
    });
  }

  fetchComputers = async () => {
    this.setState({
      pageStatus: 'loading'
    });

    const computers = await listComputers();

    this.setState(prevState => {
      return {
        pageStatus: 'success',
        computers: computers,
        progress: incrementProgress(prevState.progress),
      }
    });
  }

  addEntity = async () => {
    this.setState({
      formStatus: 'loading'
    });

    let result = await addGear(this.state.formData);

    if ('error' in result) {
      if (result.error.code === 'unique') {
        this.updateFormError(result.error.field, 'InternalId must be unique');
      }
      this.setState({ formStatus: 'error' });
    } else {

      this.setState({
        formStatus: 'success',
      }, () => {
        this.props.history.push({
          pathname: '/item'
        })
      });
    }
  }

  updateEntity = async () => {
    this.setState({
      formStatus: 'loading'
    });

    let result = await updateGear(this.state.formData, this.state.itemId);
    if ('error' in result) {
      if (result.error.code === 'unique') {
        this.updateFormError(result.error.field, 'InternalId must be unique');
      }
      this.setState({ formStatus: 'error' });
    } else {
      this.setState({
        formStatus: 'success',
        formData: {
          ...this.state.formData,
        }
      });
    }
  }

  handleSubmit = (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    ev.preventDefault();
    const isValid = this.validateForm();

    if (isValid) {
      this.setState({
        formStatus: 'loading',
      });

      if (!isEditPage(Number(this.state.itemId))) {
        this.addEntity();
      } else {
        this.updateEntity();
      }
    }
  }

  validateFormField = <K extends keyof FormData>(field: K) => {
    const subSchema = this.formSchema.extract(field);
    const result = subSchema.validate(this.state.formData[field], { abortEarly: false });

    if (result.error) {
      this.updateFormError(field, result.error.message);
    } else {
      this.updateFormError(field, "");
    }
  }

  validateForm = () => {
    // reset form errors
    this.setState({
      formErrors: {}
    });

    const result = this.formSchema.validate(this.state.formData, { abortEarly: false });
    if (result.error) {
      const formErrors = processJoiError(result.error);
      this.setState({
        // Assume type based on formSchema and Joi's error
        formErrors: formErrors as StateFormErrors,
      });

      return false;
    }

    return true;
  }

  componentDidMount() {
    this.fetchComputers();
    this.fetchUsers();

    if (isEditPage(Number(this.state.itemId))) {
      this.setState((prevState: State) => {
        return {
          progress: {
            currentStep: prevState.progress.currentStep,
            totalSteps: 3
          }
        }
      });
      this.fetchItemsDetails()
    } else {
      this.setState((prevState: State) => {
        return {
          progress: {
            currentStep: prevState.progress.currentStep,
            totalSteps: 2
          }
        }
      });
    }
  }

  updateFormError<K extends keyof StateFormErrors>(field: K, value: StateFormErrors[K]) {
    this.setState(prevState => {
      return {
        formErrors: {
          ...prevState.formErrors,
          [field]: value,
        }
      }
    })
  }

  updateFormData<K extends keyof FormData>(field: K, value: FormData[K]) {
    const formData = this.state.formData;
    this.setState({
      formData: {
        ...formData,
        [field]: value
      }
    })
  }

  setName = (ev: ChangeEvent<HTMLInputElement>) => this.updateFormData('name', ev.target.value);
  setInternalId = (ev: ChangeEvent<HTMLInputElement>) => this.updateFormData('internalId', ev.target.value);
  setAccountingId = (ev: ChangeEvent<HTMLInputElement>) => this.updateFormData('accountingId', ev.target.value);
  setUrl = (ev: ChangeEvent<HTMLInputElement>) => this.updateFormData('iconUrl', ev.target.value);
  setDescription = (ev: ChangeEvent<HTMLTextAreaElement>) => this.updateFormData('description', ev.target.value);
  setComments = (ev: ChangeEvent<HTMLTextAreaElement>) => this.updateFormData('comments', ev.target.value);
  setTileColor = (ev: ChangeEvent<HTMLInputElement>) => this.updateFormData('tileColor', ev.target.value);
  setInboundDate = (ev: ChangeEvent<HTMLInputElement>) => this.updateFormData('inboundDate', ev.target.value);
  setOutboundDate = (ev: ChangeEvent<HTMLInputElement>) => this.updateFormData('outboundDate', ev.target.value);
  setUserId = (ev: React.MouseEvent<HTMLLIElement> | KeyboardEvent, userId: number | string | undefined | UserDTO) => {this.updateFormData('userId', userId as number | undefined);}
  setComputerId = (ev: React.MouseEvent<HTMLLIElement> | KeyboardEvent, computerId: number | string | undefined | ComputerDTO) => this.updateFormData('computerId', computerId !== 'None' ? Number(computerId) : undefined);
  setDeleted = (ev: ChangeEvent<HTMLInputElement>) => this.updateFormData('deleted', ev.target.checked);


  render() {
    return (
      <React.Fragment>
        <ProgressBar
          currentStep={this.state.progress.currentStep}
          totalSteps={this.state.progress.totalSteps}
        />
        <BreadcrumbControls
          items={[
            { label: 'Items', path: '/item' },
            { label: `${!isEditPage(Number(this.state.itemId)) ? 'adding' : 'editing'} item ${this.state.formData.name}` },
          ]}
          status={this.state.pageStatus}
        />
        <div className="flex-row">
          <div className="column">
            <div className="card">
              <TextControl
                label="Name"
                type="text"
                name="name"
                id="name"
                value={this.state.formData.name}
                onChange={this.setName}
                disabled={!loadComplete(this.state.progress)}
                br={true}
                required={true}
                onBlur={ev => this.validateFormField(ev.target.name as keyof FormData)}
                error={this.state.formErrors.name}
              />
              <TextControl
                label="Internal ID"
                type="text"
                name="internalId"
                id="internalId"
                value={this.state.formData?.internalId}
                onChange={this.setInternalId}
                disabled={!loadComplete(this.state.progress)}
                br={true}
                required={true}
                onBlur={ev => this.validateFormField(ev.target.name as keyof FormData)}
                error={this.state.formErrors.internalId}
              />
              <TextControl
                label="Accounting ID"
                type="text"
                name="accountingId"
                id="accountingId"
                value={this.state.formData?.accountingId}
                onChange={this.setAccountingId}
                disabled={!loadComplete(this.state.progress)}
                br={true}
                required={true}
                onBlur={ev => this.validateFormField(ev.target.name as keyof FormData)}
                error={this.state.formErrors.accountingId}
              />
              <div className="flex-row fill">
                <div className="column">
                  <TextControl
                    label="File Name"
                    type="text"
                    name="iconUrl"
                    id="iconUrl"
                    value={this.state.formData.iconUrl}
                    onChange={this.setUrl}
                    disabled={!loadComplete(this.state.progress)}
                    br={true}
                    onBlur={ev => this.validateFormField(ev.target.name as keyof FormData)}
                  />
                </div>
                <div className="column flex-v-center">
                  <img
                    className="thumbnail padded"
                    src={process.env.REACT_APP_INVENTORY_PATH + this.state.formData.iconUrl}
                    style={{
                      "backgroundColor": this.state.formData.tileColor
                    }}
                    alt="avatar"
                  />
                </div>
              </div>
              <TextControl
                label="Tile Color"
                type="text"
                name="tileColor"
                id="tileColor"
                value={this.state.formData.tileColor}
                onChange={this.setTileColor}
                disabled={!loadComplete(this.state.progress)}
                br={true}
              />
              <TextAreaControl
                label="Description"
                id="description"
                name="description"
                value={this.state.formData.description}
                onChange={this.setDescription}
                disabled={!loadComplete(this.state.progress)}
                rows={3}
                cols={30}
                onBlur={ev => this.validateFormField(ev.target.name as keyof FormData)}
              />
              <TextAreaControl
                label="Comments"
                id="comments"
                name="comments"
                value={this.state.formData.comments}
                onChange={this.setComments}
                disabled={!loadComplete(this.state.progress)}
                rows={3}
                cols={30}
                onBlur={ev => this.validateFormField(ev.target.name as keyof FormData)}
              />
              <TextControl
                label="Inbound date"
                type="date"
                name="inboundDate"
                id="inboundDate"
                value={this.state.formData.inboundDate}
                onChange={this.setInboundDate}
                br={true}
              />
              <TextControl
                label="Outbound date"
                type="date"
                name="outboundDate"
                id="outboundDate"
                value={this.state.formData.outboundDate}
                onChange={this.setOutboundDate}
                br={true}
              />
              <SelectControl
                idName="userId"
                label="Employee"
                value={(this.state.formData.userId !== undefined && this.state.formData.userId !== null) ? this.state.formData.userId : 0}
                options={[{id: 0, fullName: 'None'} as UserDTO, ...(this.state.users || [])]}
                disabled={!loadComplete(this.state.progress)}
                onChange={this.setUserId}
                getValue={(op) => op.id}
                getLabel={(op) => op?.fullName ? op.fullName : op?.name}
                onBlur={ev => this.validateFormField(ev.target.name as keyof FormData)}
              />
              <SelectControl
                idName="computerId"
                label="Computer"
                value={(this.state.formData.computerId !== undefined && this.state.formData.computerId !== null) ? this.state.formData.computerId : 0}
                options={[{id: 0, name: 'None'} as ComputerDTO, ...(this.state.computers || [])]}
                disabled={!loadComplete(this.state.progress)}
                onChange={this.setComputerId}
                getValue={(op) => op.id}
                getLabel={(op) => op?.name}
                onBlur={ev => this.validateFormField(ev.target.name as keyof FormData)}
              />
              {isEditPage(Number(this.state.itemId)) ?
                <ToggleControl
                  id="deleted"
                  name="deleted"
                  changeMethod={this.setDeleted}
                  isChecked={this.state.formData.deleted}
                  isDisabled={!loadComplete(this.state.progress)}
                  labelText="Deleted"
                  onBlur={ev => this.validateFormField(ev.target.name as keyof FormData)}
                />
                : ''
              }
              <div className="form-group">
                <ButtonControl
                  class="primary-button"
                  onClick={(ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                    this.handleSubmit(ev)
                  }}
                  disabled={!loadComplete(this.state.progress)}
                >
                  <RequestStatus status={this.state.formStatus} />
                  <span className="text">Save</span>
                </ButtonControl>
              </div>
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  }
}

export default withTransitionEvent(ItemDetail);

ItemDetail.contextType = AppContext;